import {
  CategoryName,
  DisplayModeName,
  IInParamsValues,
} from '@infotech-soe/metaschema';

import CachedSchemaRepository from '../../../infrastructure/schemas/storage/CachedSchemaRepository';
import ISchemaRepository from '../../../infrastructure/schemas/storage/ISchemaRepository';
import { DocLinksMode } from '../../../models/docLinks/docLinks';
import DatasetLayoutStrategy from '../dataset-tree/DatasetLayoutStrategy';
import DisplayModeDatasetTreeFactory from '../dataset-tree/DisplayModeDatasetTreeFactory';
import DisplayModeIntlProvider from '../intl/DisplayModeIntlProvider';
import { CategoryTreeType } from '../view-model/CategoryViewModel';
import CategoryViewModelSource from '../view-model/CategoryViewModelSource';

import ViewModelFactory from './ViewModelFactory';

/**
 * {@link ViewModelFactory} implementation for display modes.
 */
class DisplayModeViewModelFactory extends ViewModelFactory {
  private readonly schemaRepository: ISchemaRepository;

  constructor(
    private categoryName: CategoryName,
    private displayModeName: DisplayModeName,
    private inParams?: IInParamsValues,
    private hideTree?: boolean,
    private hideCheckbox?: boolean,
    private withDefaultConfig?: boolean
  ) {
    super(
      new DisplayModeDatasetTreeFactory(categoryName, displayModeName),
      new DatasetLayoutStrategy(),
      new DisplayModeIntlProvider(categoryName, displayModeName)
    );
    this.schemaRepository = new CachedSchemaRepository();
  }

  protected async createSource(): Promise<CategoryViewModelSource> {
    const source: CategoryViewModelSource = {
      category: this.categoryName,
      displayMode: this.displayModeName,
      displayModeConditions: {},
      inParams: this.inParams,
      hideCheckbox: this.hideCheckbox,
      withDefaultConfig: this.withDefaultConfig,
    };

    const displayModeSchema = await this.schemaRepository.getDisplayMode(
      this.categoryName,
      this.displayModeName
    );

    source.displayModeHooks = {
      ...(displayModeSchema.beforeOpen && {
        open:
          displayModeSchema.beforeOpen.kind === 'conditions'
            ? 'conditions'
            : {
                type: 'action',
                action: displayModeSchema.beforeOpen.action,
              },
      }),
      ...(displayModeSchema.afterClose && {
        close: {
          type: 'action',
          action: displayModeSchema.afterClose.action,
        },
      }),
      ...(displayModeSchema.afterCancel && {
        cancel: {
          type: 'action',
          action: displayModeSchema.afterCancel.action,
        },
      }),
    };

    return source;
  }

  protected async getTreeType(): Promise<CategoryTreeType> {
    if (
      this.inParams?.linksGraph?.mode === DocLinksMode.SINGLE ||
      this.hideTree
    ) {
      return CategoryTreeType.NONE;
    }

    const displayModeSchema = await this.schemaRepository.getDisplayMode(
      this.categoryName,
      this.displayModeName
    );

    if (displayModeSchema.tree === undefined) {
      return CategoryTreeType.NONE;
    }

    switch (displayModeSchema.tree.kind) {
      case 'catalogs':
        return CategoryTreeType.CATALOGS;
      case 'hierarchy':
        return CategoryTreeType.HIERARCHY;
      default:
        return CategoryTreeType.NONE;
    }
  }
}

export default DisplayModeViewModelFactory;
